import Vue from 'vue'
import { addNamespace } from './utils'
import { getHomeworkDetails } from '@/apis/homework'
import { hasProperty } from '@/utils/object'

/**
 * @file
 * @author Thomas Grinderslev <thomas@edulab.dk>
 */

/**
 * @typedef {{homework: Object<id, Object>, idMap: Object<number, number>}} HomeworkStoreState
 */

/**
 * The types used in this store
 * @enum {string}
 */
export const HomeworkStoreTypes = {
  getters: {
    GET_HOMEWORK: 'getHomework',
    HAS_HOMEWORK_DETAILS: 'hasHomeworkDetails',
  },
  actions: {
    FETCH_HOMEWORK: 'fetchHomework',
  },
  mutations: {
    MAP_ID: 'mapId',
    SET_HOMEWORK: 'setHomework',
  },
}

/**
 * A namespaced version of the types used in this store
 * @enum {string}
 */
export const HomeworkStoreNamespacedTypes = addNamespace(
  'homework',
  HomeworkStoreTypes
)

/**
 * @returns {HomeworkStoreState}
 */
export function state() {
  return {
    homework: {},
    idMap: {},
  }
}

export const getters = {
  /**
   * @param {HomeworkStoreState} state
   * @returns {function(number):Object}
   */
  [HomeworkStoreTypes.getters.GET_HOMEWORK]: state => id => {
    return state.homework[state.idMap[id] || id] || null
  },

  /**
   * @param {HomeworkStoreState} state
   * @returns {function(number):boolean}
   */
  [HomeworkStoreTypes.getters.HAS_HOMEWORK_DETAILS]: (state, getters) => id => {
    return hasProperty(
      getters[HomeworkStoreTypes.getters.GET_HOMEWORK](id),
      'topics'
    )
  },
}

export const actions = {
  /**
   * @param {VuexCommit} commit
   * @param {VuexGetters} getters
   * @param {number} homeworkId
   * @returns {Promise}
   */
  [HomeworkStoreTypes.actions.FETCH_HOMEWORK]({ commit, getters }, homeworkId) {
    if (getters[HomeworkStoreTypes.getters.HAS_HOMEWORK_DETAILS](homeworkId)) {
      return Promise.resolve()
    }

    return getHomeworkDetails(homeworkId).then(
      ({ homework, topics, questionsCount }) => {
        if (homework.id !== homeworkId) {
          commit(HomeworkStoreTypes.mutations.MAP_ID, {
            oldId: homeworkId,
            newId: homework.id,
          })
        }

        commit(HomeworkStoreTypes.mutations.SET_HOMEWORK, {
          ...homework,
          questionCount: questionsCount,
          topics,
        })
      }
    )
  },
}

export const mutations = {
  /**
   * @param {HomeworkStoreState} state
   * @param {number} oldId
   * @param {number} newId
   */
  [HomeworkStoreTypes.mutations.MAP_ID](state, { oldId, newId }) {
    Vue.set(state.idMap, oldId, newId)
    Vue.delete(state.homework, oldId)
  },

  /**
   * @param {HomeworkStoreState} state
   * @param {Object} homework
   */
  [HomeworkStoreTypes.mutations.SET_HOMEWORK](state, homework) {
    Vue.set(state.homework, homework.id, homework)
  },
}

export default {
  namespaced: true,
  Types: HomeworkStoreTypes,
  NamespacedTypes: HomeworkStoreNamespacedTypes,
  state,
  getters,
  actions,
  mutations,
}
